import {
  PlanningEvent,
  PlanningStatus,
  PlanningSeverity,
} from "../models/planning-event.model";

/**
 * @param ofs — liste des OF
 * @param creneaux — optionnel : créneaux de planification. Si fournis, la date
 *   planifiée du créneau est utilisée en priorité sur dateDebut de l'OF.
 */
export function ofsToPlanningEvents(
  ofs: any[],
  creneaux?: any[],
): PlanningEvent[] {
  if (!ofs?.length) return [];
  const today = new Date();

  // Index créneaux par ofId pour lookup rapide
  const creneauByOfId = new Map<string, any>();
  if (creneaux?.length) {
    for (const c of creneaux) {
      if (c.ofId && c.type === "PLANIFICATION") {
        const existing = creneauByOfId.get(c.ofId);
        if (
          !existing ||
          Math.abs(new Date(c.start).getTime() - today.getTime()) <
            Math.abs(new Date(existing.start).getTime() - today.getTime())
        ) {
          creneauByOfId.set(c.ofId, c);
        }
      }
    }
  }

  return ofs
    .map((o) => {
      const creneau = creneauByOfId.get(o.id);
      const datePlanif = creneau?.start ?? o.dateDebut ?? null;
      if (!datePlanif) return null;

      const { status, severity } = deriveOfStatus(
        o.etat,
        datePlanif,
        today,
      );
      // L'API `/of/navfilter` (qui alimente `ofFacade.getAllEntities()`) renvoie
      // `articleInfos.denomination` et pas `article.denomination` eager. On
      // priorise articleInfos, avec fallback article pour les cas où l'OF est
      // chargé via `/of/id/eager` (détail).
      const article =
        o.articleInfos?.denomination ?? o.article?.denomination ?? "";
      const ligne = o.ligne?.denomination ?? "";

      const numero = o.numero ?? "?";
      return {
        id: `of:${o.id}`,
        source: "of" as const,
        kind: "start",
        reference: {
          label: article || numero,
          route: ["/productions/suivi/ope"],
          queryParams: { ofId: o.id },
        },
        datePrevu: new Date(datePlanif).toISOString(),
        dateReel: o.dateFin ? new Date(o.dateFin).toISOString() : null,
        status,
        severity,
        quantite: {
          prevu: o.quantite ?? undefined,
          reel: o.quantiteProduite ?? undefined,
          unite: "kg",
        },
        payload: {
          article,
          ligne,
          numero,
          etat: o.etat,
          etatLabel: ofEtatToLabel(o.etat),
        },
      } satisfies PlanningEvent;
    })
    .filter((e) => e !== null) as PlanningEvent[];
}

function deriveOfStatus(
  etat: string,
  dateDebut: Date | string | null,
  today: Date,
): { status: PlanningStatus; severity: PlanningSeverity } {
  switch (etat) {
    case "ANNULE":
      return { status: "cancelled", severity: 0 };
    case "VALIDE":
      return { status: "completed", severity: 0 };
    case "EN_PROD":
      return { status: "in_progress", severity: 0 };
    case "EN_ATTENTE_BILAN_MATIERE":
    case "EN_ATTENTE_REINTE_PDC":
    case "EN_ATTENTE_VALIDATION":
      return { status: "partial", severity: 1 };
    case "EN_ATTENTE_PROD":
    case "EN_ATTENTE_PLANNIF": {
      if (dateDebut) {
        const d = new Date(dateDebut);
        d.setHours(23, 59, 59, 999);
        if (d < today) return { status: "late", severity: 2 };
      }
      return { status: "pending", severity: 0 };
    }
    default:
      return { status: "pending", severity: 0 };
  }
}

function ofEtatToLabel(etat: string): string {
  const labels: Record<string, string> = {
    EN_ATTENTE_PLANNIF: "Att. plannif.",
    EN_ATTENTE_PROD: "Att. prod.",
    EN_PROD: "En production",
    EN_ATTENTE_BILAN_MATIERE: "Att. bilan mat.",
    EN_ATTENTE_REINTE_PDC: "Att. réintég.",
    EN_ATTENTE_VALIDATION: "Att. validation",
    VALIDE: "Validé",
    ANNULE: "Annulé",
  };
  return labels[etat] ?? etat;
}
